import { categoryLabels } from "../opportunities/api.js";
import { Field, inputClass } from "../routes/SignIn.js";
import { ServiceAreaMap } from "../serviceArea/ServiceAreaMap.js";

/**
 * Shared business-profile fields for the application form (PortalApply) and
 * the APPROVED profile editor (PortalProfile). One component so the two
 * forms can't drift apart; the apply variant adds the business-email and
 * experience fields via `showIdentity`.
 *
 * Service area is captured on the map (build-plan: service-area map) as a
 * set of selected TOWN keys; the caller expands it to the canonical ZIP set
 * with `zipsForTownKeys` and the display labels with `cityLabelsForTownKeys`.
 */
export interface ProfileFormState {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  yearsExperience: string;
  serviceCategories: string[];
  /** Selected town keys ("City|ST") from the service-area map. */
  selectedTowns: string[];
  licenseInfo: string;
  insuranceInfo: string;
  about: string;
}

export function emptyProfileForm(): ProfileFormState {
  return {
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    website: "",
    yearsExperience: "",
    serviceCategories: [],
    selectedTowns: [],
    licenseInfo: "",
    insuranceInfo: "",
    about: "",
  };
}

export function ProfileFormFields({
  state,
  onChange,
  showIdentity,
}: {
  state: ProfileFormState;
  onChange: (patch: Partial<ProfileFormState>) => void;
  showIdentity: boolean;
}) {
  function toggleCategory(category: string) {
    onChange({
      serviceCategories: state.serviceCategories.includes(category)
        ? state.serviceCategories.filter((c) => c !== category)
        : [...state.serviceCategories, category],
    });
  }

  return (
    <>
      <Field label="Business name">
        <input
          type="text"
          value={state.businessName}
          onChange={(e) => onChange({ businessName: e.target.value })}
          autoComplete="organization"
          required
          className={inputClass}
        />
      </Field>
      <Field label="Contact name">
        <input
          type="text"
          value={state.contactName}
          onChange={(e) => onChange({ contactName: e.target.value })}
          autoComplete="name"
          required
          className={inputClass}
        />
      </Field>
      {showIdentity && (
        <div>
          <Field label="Business email">
            <input
              type="email"
              value={state.email}
              onChange={(e) => onChange({ email: e.target.value })}
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              required
              className={inputClass}
            />
          </Field>
          {/* Hint lives OUTSIDE the <label> so label-text queries (and screen
              readers) get the clean field name. */}
          <p className="mt-1 text-xs text-charcoal">
            Where job invitations are sent. Pre-filled with your login email.
          </p>
        </div>
      )}
      <Field label="Phone">
        <input
          type="tel"
          value={state.phone}
          onChange={(e) => onChange({ phone: e.target.value })}
          inputMode="tel"
          autoComplete="tel"
          required
          className={inputClass}
        />
      </Field>
      <Field label="Website (optional)">
        <input
          type="url"
          value={state.website}
          onChange={(e) => onChange({ website: e.target.value })}
          inputMode="url"
          placeholder="https://"
          className={inputClass}
        />
      </Field>
      {showIdentity && (
        <Field label="Years in business">
          <input
            type="text"
            value={state.yearsExperience}
            onChange={(e) => onChange({ yearsExperience: e.target.value.replace(/\D/g, "") })}
            inputMode="numeric"
            pattern="[0-9]*"
            required
            className={inputClass}
          />
        </Field>
      )}

      <fieldset>
        <legend className="mb-2 block text-sm font-medium text-charcoal">Services offered</legend>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(categoryLabels).map(([value, label]) => {
            const checked = state.serviceCategories.includes(value);
            return (
              <label
                key={value}
                className={[
                  "flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border px-3 text-sm font-medium",
                  checked
                    ? "border-brand-700 bg-brand-50 text-brand-800"
                    : "border-charcoal/30 bg-white text-charcoal",
                ].join(" ")}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCategory(value)}
                  className="h-4 w-4 accent-brand-700"
                />
                {label}
              </label>
            );
          })}
        </div>
      </fieldset>

      <ServiceAreaMap
        selected={state.selectedTowns}
        onChange={(selectedTowns) => onChange({ selectedTowns })}
      />

      <Field label="License info (optional)">
        <input
          type="text"
          value={state.licenseInfo}
          onChange={(e) => onChange({ licenseInfo: e.target.value })}
          className={inputClass}
        />
      </Field>
      <Field label="Insurance info (optional)">
        <input
          type="text"
          value={state.insuranceInfo}
          onChange={(e) => onChange({ insuranceInfo: e.target.value })}
          className={inputClass}
        />
      </Field>
      <Field label="About your business (optional)">
        <textarea
          value={state.about}
          onChange={(e) => onChange({ about: e.target.value })}
          rows={4}
          maxLength={2000}
          className={inputClass + " min-h-[88px] py-3"}
        />
      </Field>
    </>
  );
}
